import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, query, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { StaggeredLoadingWrapper } from './ui/withLoading';
import { SkeletonConversationTile, SkeletonList } from './ui/skeletons';
import { AnimatedContainer, AnimatedItem } from './ui/animated';
import { checkSubscriptionSilently } from '../lib/utils';
import PageWrapper from './PageWrapper';
import './ChatsList.css';
import { debugLog, errorLog } from '../utils/debug';

export default function ChatsList() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (currentUser) {
      // Silently check subscription status
      checkSubscriptionSilently(currentUser, navigate).then(isValid => {
        if (isValid) {
          loadChats();
        }
      });
    }
  }, [currentUser]);

  useEffect(() => {
    // Add entrance animations
    const tiles = document.querySelectorAll('.conversation-tile');
    
    tiles.forEach((tile, index) => {
      tile.style.opacity = '0';
      tile.style.transform = 'translateY(20px)';
      tile.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
      
      setTimeout(() => {
        tile.style.opacity = '1';
        tile.style.transform = 'translateY(0)';
      }, 100 + (index * 50));
    });
  }, [chats]);

  const loadChats = async () => {
    setLoading(true);
    try {
      debugLog('🔥 Loading chats from Firebase subcollection (sole source of truth)...');
      const db = getFirestore();
      
      // Query the users/{uid}/chats subcollection directly
      const userChatsRef = collection(db, 'users', currentUser.uid, 'chats');
      const chatsQuery = query(
        userChatsRef,
        orderBy('endedAt', 'desc'),
        limit(50)
      );
      
      const querySnapshot = await getDocs(chatsQuery);
      const chats = [];
      
      querySnapshot.forEach((doc) => {
        const chatData = doc.data();
        debugLog('📄 Chat document data:', doc.id, chatData);
        
        // Only include chats with ready status and valid summaries
        if (chatData.status === 'ready' && chatData.summary) {
          chats.push({
            id: doc.id,
            status: chatData.status,
            startedAt: chatData.startedAt,
            endedAt: chatData.endedAt,
            summary: chatData.summary,
            timestamp: chatData.endedAt || chatData.updatedAt, // For compatibility
            updatedAt: chatData.updatedAt,
            meta: chatData.meta
          });
        } else if (chatData.status === 'pending' || chatData.status === 'processing') {
          // Also include pending/processing chats so user can see them
          chats.push({
            id: doc.id,
            status: chatData.status,
            startedAt: chatData.startedAt,
            endedAt: chatData.endedAt,
            summary: null, // No summary yet
            timestamp: chatData.endedAt || chatData.updatedAt,
            updatedAt: chatData.updatedAt,
            meta: chatData.meta
          });
        }
      });
      
      debugLog(`✅ Loaded ${chats.length} chats from Firebase subcollection`);
      debugLog('📋 Chat data:', chats);
      setChats(chats);
      
    } catch (err) {
      errorLog('❌ Error loading chats from Firebase:', err);
      setError('Error loading chats from Firestore');
      setChats([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateDaysSince = (endDate) => {
    if (!endDate) return 'Unknown';
    
    let date;
    try {
      // Handle Firestore Timestamp objects
      if (endDate && typeof endDate === 'object' && endDate.toDate) {
        date = endDate.toDate();
      } else if (endDate && typeof endDate === 'object' && endDate.seconds) {
        // Handle Firestore Timestamp with seconds
        date = new Date(endDate.seconds * 1000);
      } else {
        // Handle string dates
        date = new Date(endDate);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        debugWarn('⚠️ Invalid date for days calculation:', endDate);
        return 'Unknown';
      }
      
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return `${Math.floor(diffDays / 365)} years ago`;
      
    } catch (error) {
      errorLog('❌ Error calculating days since:', error, 'Input:', endDate);
      return 'Unknown';
    }
  };

  const getRelativeTime = (date) => {
    if (!date) return 'unknown';
    
    const now = new Date();
    const chatDate = new Date(date);
    const diffMs = now - chatDate;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return '1d';
    if (diffDays < 7) return `${diffDays}d`;
    if (diffDays < 14) return '1w';
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w`;
    if (diffDays < 60) return '1m';
    return `${Math.floor(diffDays / 30)}m`;
  };

  const countInsights = (summary) => {
    if (!summary) return 0;
    
    const insights = summary.key_insights || summary.keyInsights || [];
    return insights.length;
  };

  const handleBack = () => {
    navigate('/homepage');
  };

  const handleChatClick = (chatId) => {
    navigate(`/sessionsummary/${chatId}`);
  };

  // Add click feedback for back button and conversation tiles
  const handleBackClick = (e) => {
    const target = e.target;
    target.style.transform = 'scale(0.9)';
    setTimeout(() => {
      target.style.transform = '';
      navigate('/homepage');
    }, 100);
  };

  const handleTileClick = (chatId, e) => {
    const target = e.currentTarget;
    target.style.transform = 'scale(0.98)';
    setTimeout(() => {
      target.style.transform = '';
      navigate(`/sessionsummary/${chatId}`);
    }, 100);
  };

  if (loading) {
    return (
      <div className="chats-container">
        <div className="floating-elements">
          <div className="floating-circle circle-1"></div>
          <div className="floating-circle circle-2"></div>
          <div className="floating-circle circle-3"></div>
        </div>
        <div className="phone-container">
          <div className="content">
            <div className="header">
              <button className="back-btn" onClick={handleBackClick}>←</button>
              <h1 className="page-title">History</h1>
            </div>
            <StaggeredLoadingWrapper 
              loading={loading} 
              skeleton={
                <div className="conversation-list">
                  {Array.from({ length: 8 }).map((_, index) => (
                    <div
                      key={index}
                      className="conversation-tile"
                      style={{ 
                        backgroundColor: '#E6F4EA',
                        border: '1px solid rgba(18, 51, 47, 0.1)',
                        cursor: 'default'
                      }}
                    >
                      <div className="conversation-header">
                        <div className="conversation-title">
                          <div className="h-5 rounded w-3/4" style={{ backgroundColor: 'rgba(18, 51, 47, 0.2)' }} />
                        </div>
                        <div className="conversation-date">
                          <div className="h-4 rounded w-20" style={{ backgroundColor: 'rgba(142, 142, 147, 0.3)' }} />
                        </div>
                      </div>
                      <div className="conversation-preview">
                        <div className="h-4 rounded w-full mb-2" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
                        <div className="h-4 rounded w-2/3" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
                      </div>
                      <div className="conversation-meta">
                        <div className="insights-count">
                          <div className="h-4 rounded w-20" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              }
            >
              <div className="conversation-list">
                <div className="loading-state">
                  <div className="loading-icon">📊</div>
                  <div className="loading-text">Loading your chat history...</div>
                </div>
              </div>
            </StaggeredLoadingWrapper>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PageWrapper>
      <div className="chats-container">
        <div className="floating-elements">
          <div className="floating-circle circle-1"></div>
          <div className="floating-circle circle-2"></div>
          <div className="floating-circle circle-3"></div>
        </div>

        <div className="phone-container">
        
        <div className="content">
          {/* Header */}
          <div className="header">
            <button className="back-btn" onClick={handleBackClick}>←</button>
            <h1 className="page-title">History</h1>
          </div>

          {/* Conversation List */}
          <StaggeredLoadingWrapper 
            loading={loading} 
            skeleton={
              <div className="conversation-list">
                {Array.from({ length: 8 }).map((_, index) => (
                  <div
                    key={index}
                    className="conversation-tile"
                    style={{ 
                      backgroundColor: '#E6F4EA',
                      border: '1px solid rgba(18, 51, 47, 0.1)',
                      cursor: 'default'
                    }}
                  >
                    <div className="conversation-header">
                      <div className="conversation-title">
                        <div className="h-5 rounded w-3/4" style={{ backgroundColor: 'rgba(18, 51, 47, 0.2)' }} />
                      </div>
                      <div className="conversation-date">
                        <div className="h-4 rounded w-20" style={{ backgroundColor: 'rgba(142, 142, 147, 0.3)' }} />
                      </div>
                    </div>
                    <div className="conversation-preview">
                      <div className="h-4 rounded w-full mb-2" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
                      <div className="h-4 rounded w-2/3" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
                    </div>
                    <div className="conversation-meta">
                      <div className="insights-count">
                        <div className="h-4 rounded w-20" style={{ backgroundColor: 'rgba(127, 176, 105, 0.15)' }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            }
          >
            <div className="conversation-list">
              {error && (
                <div className="empty-state">
                  <div className="empty-icon">⚠️</div>
                  <div className="empty-title">Error Loading Chats</div>
                  <div className="empty-subtitle">{error}</div>
                </div>
              )}

              {!error && chats.length === 0 && (
                <div className="empty-state">
                  <div className="empty-icon">💭</div>
                  <div className="empty-title">No Chat History Yet</div>
                  <div className="empty-subtitle">
                    Your chat summaries will appear here after you save conversations.
                  </div>
                </div>
              )}

              <AnimatedContainer className="conversation-list">
                {chats.map((chat, index) => (
                  <AnimatedItem key={chat.id || index}>
                    <div
                      className="conversation-tile"
                      data-id={chat.id}
                      onClick={(e) => handleTileClick(chat.id, e)}
                    >
                      <div className="conversation-header">
                        <div className="conversation-title">
                          {chat.summary?.title || 'Chat Session'}
                        </div>
                        <div className="conversation-date">
                          {calculateDaysSince(chat.timestamp || chat.ended_at)}
                        </div>
                      </div>
                      <div className="conversation-preview">
                        {chat.summary?.public_summary || 'Summary processing...'}
                      </div>
                      <div className="conversation-meta">
                        <div className="insights-count">
                          {countInsights(chat.summary)} insights
                        </div>
                      </div>
                    </div>
                  </AnimatedItem>
                ))}
              </AnimatedContainer>
            </div>
          </StaggeredLoadingWrapper>
        </div>
        </div>
      </div>
    </PageWrapper>
  );
}